import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ConfigProvider, message, Modal, Select, Spin } from "antd";
import ProjectService, {
  ProjectDto,
  DeliverableDto,
  TenantInfoDto,
} from "../../services/ProjectService";
import DeliverableService from "../../../DeliverableView/services/DeliverableService";
import TierDiagram from "../../../TieringView/components/TierDiagram/TierDiagram";
import { ACTIVE_TABS_KEY, NAME_KEY, MULTI_SHEET_KEY } from "../../../../shared/utils/CommonConstants";
import { darkFormTheme, MODAL_STYLES, MODAL_TITLE_STYLE } from "../../../../styles/modalStyles";
import styles from "./ProjectDeliverables.module.css";

const projectService = new ProjectService();
const deliverableService = new DeliverableService();

const TABS = [
  { key: "calculations", label: "Calculations" },
  { key: "reports", label: "Reports" },
  { key: "tiering", label: "Tiering" },
];

export const ProjectDeliverables: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<ProjectDto | null>(null);
  const [tenantInfo, setTenantInfo] = useState<TenantInfoDto | null>(null);
  const [deliverables, setDeliverables] = useState<DeliverableDto[]>([]);
  const [activeTab, setActiveTab] = useState("calculations");
  const [isLoading, setIsLoading] = useState(false);

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [unassignedDeliverables, setUnassignedDeliverables] = useState<DeliverableDto[]>([]);
  const [selectedDeliverableId, setSelectedDeliverableId] = useState<number | null>(null);
  const [isAssigning, setIsAssigning] = useState(false);

  const fetchData = useCallback(async () => {
    if (!projectId) return;
    setIsLoading(true);
    try {
      const [proj, delivs, tenant] = await Promise.all([
        projectService.getProjectById(Number(projectId)),
        projectService.getProjectDeliverables(Number(projectId)),
        projectService.getTenantInfo(),
      ]);
      setProject(proj);
      setDeliverables(delivs);
      setTenantInfo(tenant);
    } catch {
      message.error("Failed to load project data");
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openDeliverable = async (deliverable: DeliverableDto) => {
    try {
      const sheets = await deliverableService.getDeliverableMenuDetails<
        { sheetId: number; sheetName: string; multiSheet: boolean }[]
      >(deliverable.id);
      if (sheets && sheets.length > 0) {
        const firstSheet = sheets[0];
        let url = `/deliverable/${deliverable.id}/${firstSheet.sheetId}?${ACTIVE_TABS_KEY}=${firstSheet.sheetName}&${NAME_KEY}=${deliverable.name}`;
        if (firstSheet.multiSheet) {
          url += `&${MULTI_SHEET_KEY}=true`;
        }
        navigate(url, { state: { fromProjectId: projectId } });
      } else {
        navigate(
          `/deliverable/${deliverable.id}?${ACTIVE_TABS_KEY}=Dashboard&${NAME_KEY}=${deliverable.name}`,
          { state: { fromProjectId: projectId } }
        );
      }
    } catch {
      // Silently navigate to deliverable overview when sheet list is unavailable
      navigate(
        `/deliverable/${deliverable.id}?${ACTIVE_TABS_KEY}=Dashboard&${NAME_KEY}=${deliverable.name}`,
        { state: { fromProjectId: projectId } }
      );
    }
  };

  const handleOpenAssignModal = async () => {
    setShowAssignModal(true);
    try {
      const unassigned = await projectService.getUnassignedDeliverables();
      setUnassignedDeliverables(unassigned);
    } catch {
      message.error("Failed to load unassigned deliverables");
    }
  };

  const handleAssign = async () => {
    if (!selectedDeliverableId || !projectId) return;
    setIsAssigning(true);
    try {
      await projectService.assignDeliverableToProject(
        Number(projectId),
        selectedDeliverableId
      );
      message.success("Deliverable assigned to project");
      setShowAssignModal(false);
      setSelectedDeliverableId(null);
      fetchData();
    } catch {
      message.error("Failed to assign deliverable");
    } finally {
      setIsAssigning(false);
    }
  };

  return (
    <>
      {/* Breadcrumb strip */}
      <div className={styles.breadcrumbStrip}>
        <span onClick={() => navigate("/projects")}>
          {tenantInfo?.tenantName || "Client"}
        </span>
        {" / "}
        <span>{project?.entityName || "Entity"}</span>
        {" / "}
        <span>{project?.engagementType || "Engagement"}</span>
        {" / "}
        <span>{project?.taxYear}</span>
      </div>

      {/* Content panel */}
      <div className={styles.contentPanel}>
        {/* Entity Header */}
        <div className={styles.entityHeader}>
          <div className={styles.entityClient}>{tenantInfo?.tenantName}</div>
          <div className={styles.entityTitleRow}>
            <div className={styles.entityName}>
              {project?.entityName || "Entity"}
            </div>
            <div className={styles.entityMeta}>
              <span className={styles.entityMetaItem}>{project?.engagementType}</span>
              <span className={styles.entityMetaItem}>{project?.taxYear}</span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <nav className={styles.tabNav}>
          {TABS.map((tab) => (
            <button
              key={tab.key}
              className={activeTab === tab.key ? styles.tabItemActive : styles.tabItem}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        {/* Tiering tab — React Flow hierarchy diagram */}
        {activeTab === "tiering" && (
          <div className={styles.deliverableArea}>
            <TierDiagram rootEntityId={project?.entityId ?? 0} />
          </div>
        )}

        {/* Deliverable Cards — shown on all non-tiering tabs */}
        {activeTab !== "tiering" && (
        <div className={styles.deliverableArea}>
          <div className={styles.deliverableAreaHeader}>
            <button className={styles.btnAssign} onClick={handleOpenAssignModal}>
              + Assign Deliverable
            </button>
          </div>

          {isLoading ? (
            <div className={styles.loadingState}><Spin /></div>
          ) : deliverables.length === 0 ? (
            <div className={styles.emptyState}>
              No deliverables assigned to this project yet. Click "Assign Deliverable" to link existing deliverables.
            </div>
          ) : (
            deliverables.map((deliverable) => (
              <div key={deliverable.id} className={styles.deliverableCard}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardTitle}>{deliverable.name}</div>
                  <button
                    className={styles.btnViewDetails}
                    onClick={() => openDeliverable(deliverable)}
                  >
                    View Details
                  </button>
                </div>
                <div className={styles.cardDescription}>
                  Allocate income using Target Allocation Method
                </div>
                <div className={styles.metricsRow}>
                  <div className={styles.metricItem}>
                    <div className={styles.metricLabel}>Number of{"\n"}Partner Interest</div>
                    <div className={styles.metricValue}>10</div>
                  </div>
                  <div className={styles.metricItem}>
                    <div className={styles.metricLabel}>Taxable{"\n"}Income</div>
                    <div className={styles.metricValue}>$ 2,000,000</div>
                  </div>
                  <div className={styles.metricItem}>
                    <div className={styles.metricLabel}>Sec. 704(b){"\n"}Income</div>
                    <div className={styles.metricValue}>$ 2,000,000</div>
                  </div>
                  <div className={styles.metricItem}>
                    <div className={styles.metricLabel}>Tax{"\n"}Depreciation</div>
                    <div className={styles.metricValue}>$ 200,000</div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        )}
      </div>

      {/* Assign Deliverable Modal */}
      <Modal
        title={<span style={MODAL_TITLE_STYLE}>Assign Deliverable to Project</span>}
        open={showAssignModal}
        onOk={handleAssign}
        onCancel={() => { setShowAssignModal(false); setSelectedDeliverableId(null); }}
        okText="Assign"
        okButtonProps={{ disabled: !selectedDeliverableId, loading: isAssigning }}
        styles={MODAL_STYLES}
      >
        <p style={{ marginBottom: 12, color: "#ADADAD", fontSize: 13 }}>
          Select an unassigned deliverable to link to this project:
        </p>
        <ConfigProvider theme={darkFormTheme}>
          <Select
            style={{ width: "100%" }}
            placeholder="Select a deliverable"
            value={selectedDeliverableId}
            onChange={(val) => setSelectedDeliverableId(val)}
            options={unassignedDeliverables.map((d) => ({
              value: d.id,
              label: d.name,
            }))}
            showSearch
            filterOption={(input, option) =>
              (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
            }
          />
        </ConfigProvider>
      </Modal>
    </>
  );
};
